// Nested layout route for /admin/:slug/*. Mounts once on entry to a
// station's admin views and stays mounted across tab clicks within the
// same station — the previous architecture re-mounted the gate and the
// layout on every tab because each page wrapped them itself.
//
// Responsibilities:
//   • Owns PublisherAdminGate (auth + station-access + grant check).
//   • Owns PublisherAdminLayout (sidebar + main content shell).
//   • Provides the outlet context (mode='single' today; mode='aggregated'
//     when /admin/all/* is built).
//
// Child routes render only their unique content; they read config + slug
// + user via useSingleStationContext() from ./useAdminContext.

import React from 'react';
import { Outlet, useParams } from 'react-router-dom';
import PublisherAdminGate from './PublisherAdminGate';
import PublisherAdminLayout from './PublisherAdminLayout';
import type { AdminContext } from './useAdminContext';

const AdminShell: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // FUTURE: if (slug === 'all') return <AggregatedAdminShell />.
  // Aggregated mode loads useAuth.myAdminStations, provides
  // mode='aggregated' outlet context, and renders a sidebar variant
  // without a current-station header. Not built today; the gate's
  // slug-not-found redirect handles /admin/all cleanly until then.
  return (
    <PublisherAdminGate slug={slug}>
      {({ config, user, slug: gatedSlug }) => {
        const context: AdminContext = {
          mode: 'single',
          config,
          slug: gatedSlug,
          user: {
            id: user.id ?? '',
            email: user.email ?? '',
            rawRole: (user as { rawRole?: string }).rawRole,
          },
        };
        return (
          <PublisherAdminLayout config={config}>
            <Outlet context={context} />
          </PublisherAdminLayout>
        );
      }}
    </PublisherAdminGate>
  );
};

export default AdminShell;
